<template>
  <sub-page
    v-if="hasFailedSalaryEmployees"
    :title="$t('payschedules.failedSalaries.title')"
    :subtitle="$t('payschedules.failedSalaries.subtitle')"
  >
    <sme-alert :level="ALERT_LEVELS.INFO" class="mb-3" v-if="showAchFailedSalaryExplainer">
      {{ $t('payschedules.failedSalaries.explainerAlert') }}
    </sme-alert>
    <header class="d-flex flex-column flex-sm-row align-items-end mb-3">
      <sme-alert :level="ALERT_LEVELS.DANGER" class="mb-3 mb-sm-0">
        <i18n-t path="payschedules.failedSalaries.dangerAlert" tag="span">
          <template #emailLink
            ><a :href="`mailto:${supportEmail}`">{{ supportEmail }}</a></template
          >
        </i18n-t>
      </sme-alert>
      <export-button
        :file-name="exportFilename"
        :export-data="failedSalaryEmployees"
        :export-file-rows="exportFileRows"
        :export-headers="EXPORT_HEADERS.value || EXPORT_HEADERS"
      />
    </header>

    <sme-detailed-table
      :fields="FIELDS.value || FIELDS"
      :items="failedSalaryEmployees"
      sort-by="full_name"
      tbody-tr-class="sme-detailed-table__row--link"
      sort-asc
      sticky-header
      @row-clicked="item => $router.push({ name: 'employee', params: { employeeId: item.employee_id } })"
    >
      <template #cell(details)="data">
        <div class="sme-detailed-table__cell sme-detailed-table__cell--wide">
          <strong class="sme-detailed-table__label sme-detailed-table__label--large">
            {{ data.item.employee_name }}
          </strong>
          <span class="sme-detailed-table__value sme-detailed-table__value--primary">
            {{ data.item.employee_code }}
          </span>
        </div>
      </template>

      <template #cell(rejection_reasons)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{ $t('payschedules.failedSalaries.rejectionReasonsLabel') }}</span>
          <strong class="sme-detailed-table__value">{{
            toTitleCase(data.item.rejection_reasons.join(' ').replace('-', ' '))
          }}</strong>
        </div>
      </template>

      <template #cell(account_number)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{
            $t('payschedules.failedSalaries.accountNumberLabel', {
              accountNumberLabel: bankingInfo.accountNumber.label,
            })
          }}</span>
          <strong class="sme-detailed-table__value">{{ data.item.account_number }}</strong>
        </div>
      </template>

      <template #cell(sort_code)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{
            $t('payschedules.failedSalaries.sortCodeLabel', { sortCodeLabel: bankingInfo.sortCode.label })
          }}</span>
          <strong class="sme-detailed-table__value">{{ data.item.sort_code }}</strong>
        </div>
      </template>
    </sme-detailed-table>
  </sub-page>
</template>

<script setup>
import moment from 'moment/moment';
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import ExportButton from '@/components/ExportButton.vue';
import SubPage from '@/components/SubPage.vue';
import SmeAlert, { ALERT_LEVELS } from '@/components/atoms/SmeAlert';
import SmeDetailedTable from '@/components/atoms/SmeDetailedTable.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import i18n from '@/i18n';
import { getBankingInfo } from '@/Banking';
import State from '@/state/State';
import usePayschedulesReport from '@/state/composables/usePayschedulesReport';
import { toTitleCase } from '@/utils/common';
import { getCompanySupportEmail } from '@/utils/support';

const $router = useRouter();
const { failedSalaryEmployees, hasFailedSalaryEmployees } = usePayschedulesReport();
const { showAchFailedSalaryExplainer } = useFeatureFlags();
const bankingInfo = getBankingInfo();

const supportEmail = computed(() => getCompanySupportEmail());

// computed so that i18n.t() runs on every locale switch
const FIELDS = computed(() => [
  {
    key: 'details',
    label: i18n.t('payschedules.employeeNameHeader'),
  },
  {
    key: 'rejection_reasons',
    label: i18n.t('payschedules.failedSalaries.rejectionReasonsHeader'),
  },
  {
    key: 'account_number',
    label: i18n.t('payschedules.failedSalaries.accountNumberHeader', {
      accountNumberLabel: bankingInfo.accountNumber.label,
    }),
  },
  {
    key: 'sort_code',
    label: i18n.t('payschedules.failedSalaries.sortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
  },
]);

const EXPORT_HEADERS = computed(() => [
  i18n.t('payschedules.failedSalaries.exportEmployeeCodeHeader'),
  i18n.t('payschedules.employeeNameHeader'),
  i18n.t('payschedules.failedSalaries.exportPersonalSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
  i18n.t('payschedules.failedSalaries.exportPersonalAccountNoHeader', {
    accountNumberLabel: bankingInfo.accountNumber.label,
  }),
  i18n.t('payschedules.failedSalaries.exportStreamSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
  i18n.t('payschedules.failedSalaries.exportStreamAccountNoHeader', {
    accountNumberLabel: bankingInfo.accountNumber.label,
  }),
]);

const exportFilename = computed(() => {
  const today = moment().format('YYYY-MM-DD');
  let companyName = '';
  if (State.state.company.name) {
    companyName = State.state.company.name.toLowerCase()?.replace(/ /g, '-');
  }

  return `payroll-update-list-${companyName}-${today}`;
});

const exportFileRows = item => {
  return [
    item.employee_code,
    item.full_name,
    item.beneficiary_sort_code,
    item.beneficiary_account_number,
    item.payment_sort_code,
    item.payment_account_number,
  ];
};
</script>
